import { createSteeringURL } from '@/api'
import { useI18n } from '@/i18n'
import { steeringAuthInit, useOnAuthBehaviour } from '@/stores/auth'
import { RRule } from '@/types'
import { humanJoin as _humanJoin, APIConsume, useRetrieveHelpers } from '@/util'
import { APIListPaginated, APIRetrieve, createExtendableAPI } from '@rokoli/bnb/drf'
import { defineStore } from 'pinia'
import { RRule as RRuleObject } from 'rrule'
import type { ParsedOptions } from 'rrule/dist/esm/types'
import { match, P } from 'ts-pattern'
import { computed, MaybeRefOrGetter, toValue, watch } from 'vue'

export const useRRuleStore = defineStore('rrules', () => {
  const endpoint = createSteeringURL.prefix('rrules')
  const { api, base } = createExtendableAPI<RRule>(endpoint, steeringAuthInit)
  const listOperations = APIListPaginated(api)
  const consumeOperations = APIConsume(listOperations.listIsolated)
  const retrieveOperations = APIRetrieve(api)
  const { locale } = useI18n()

  function updateCache() {
    void consumeOperations.consume({
      requestInit: { headers: { 'Accept-Language': `${locale.value}, *` } },
    })
  }

  useOnAuthBehaviour(updateCache)
  watch(locale, updateCache)

  return {
    ...base,
    ...listOperations,
    ...consumeOperations,
    ...retrieveOperations,
    ...useRetrieveHelpers({ ...base, ...retrieveOperations }),
  }
})

export function useRRuleLabel(
  rrule: MaybeRefOrGetter<Partial<RRule> | null | undefined>,
  override?: MaybeRefOrGetter<{ byWeekday: number | string | null } | undefined | null> | undefined,
) {
  const { dayNames, t } = useI18n()
  const frequency = { yearly: 0, monthly: 1, weekly: 2, daily: 3 }

  function humanJoin(iterable: Iterable<string>, mode: 'and' | 'or' = 'and') {
    return _humanJoin(iterable, t('humanJoin.default'), t(`humanJoin.${mode}`))
  }

  function getByWeekdayLabel(weekdays: number[], select: 'first' | 'last' | undefined = undefined) {
    const days = match(weekdays)
      .with([0, 1, 2, 3, 4], () => t('rrule.businessDay', { smart_count: select ? 1 : 0 }))
      .otherwise((v) => {
        const days = [...v].sort()
        if (days.length === 0) return ''
        if (select === 'first') {
          return dayNames.value[days[0] as number] as string
        } else if (select === 'last') {
          return dayNames.value[days.at(-1) as number] as string
        } else {
          return humanJoin(days.map((index) => dayNames.value[index] as string)).trim()
        }
      })
    // select has its own description for days
    if (select) {
      return days
    }
    // weekdays might be an empty array, in which case we don’t want to translate
    return days ? t('rrule.onDays', { days }) : ''
  }

  /**
   * Generates a human-readable label for a recurrence rule.
   *
   * This function only supports a subset of the features rrule provide, because supporting
   * the entirety is not easy, especially if internationalization is a concern.
   * Instead, it only implements the use-cases we have defined within AURA.
   *
   * @param options - The normalized recurrence rule options to generate the label for.
   * @return The label for the options.
   */
  function getNaturalLanguageRRuleRepresentation(options: ParsedOptions): string {
    // this one is easy
    if (options.count === 1) {
      return t('rrule.once')
    }

    // these attributes are unsupported in our rudimentary implementation
    if (
      (options.bymonthday ?? []).length > 0 ||
      (options.bynmonthday ?? []).length > 0 ||
      (options.bymonth ?? []).length > 0 ||
      (options.byyearday ?? []).length > 0 ||
      (options.byweekno ?? []).length > 0
    ) {
      throw new Error('Unsupported attribute in rrule.')
    }

    const bySetPos = options.bysetpos ?? []
    const byWeekday = options.byweekday ?? []

    if (bySetPos.length > 1) {
      throw new Error('Multiple values for bysetpos are not supported.')
    }

    // find the frequency first.
    // run() will throw if we’ve got something more granular than day, which is intended because it’s unsupported.
    const freqLabel = match(options.freq)
      .with(frequency.yearly, () => t('rrule.yearly', { smart_count: options.interval }))
      .with(frequency.monthly, () => t('rrule.monthly', { smart_count: options.interval }))
      .with(frequency.weekly, () => t('rrule.weekly', { smart_count: options.interval }))
      .with(frequency.daily, () => t('rrule.daily', { smart_count: options.interval }))
      .run()

    const bySetPosLabel = humanJoin(
      bySetPos.map((pos) => {
        if (![-1, 1, 2, 3, 4, 5].includes(pos)) {
          throw new Error(`Unsupported bysetpos value in rrule: ${pos}`)
        }
        return t(`rrule.position.${pos}`)
      }),
    )

    const byWeekdayLabel =
      options.freq !== frequency.daily
        ? getByWeekdayLabel(
            byWeekday,
            // In case bySetPos has been used, the first instance of a match
            // is used for the match in the specified frequency.
            // Returning all possible matches is unnecessary.
            //
            // Example: freq=month, bysetpos=-1, byweekday=[2,4]
            // If this rrule would be applied it would select the last Friday (4) of every month,
            // though by default getByWeekdayLabel would return `every month on the last Wednesday, and Friday`.
            // This would be wrong, because bysetpos only selects the first matching entry, which in this case,
            // would be Friday.
            match(bySetPos[0])
              .with(P.number.negative(), () => 'last' as const)
              .with(P.number.positive(), () => 'first' as const)
              .with(P.number, () => undefined)
              .with(P.nullish, () => undefined)
              .exhaustive(),
          )
        : ''

    return [freqLabel, bySetPosLabel, byWeekdayLabel]
      .map((label) => label.trim())
      .filter((label) => label !== '')
      .join(' ')
  }

  // Creates a rrule.js rrule-object. This is helpful, because it normalizes the properties
  // we derive from our own models, and we may catch some edge cases in tests that only surface
  // when actual rrule objects are processed.
  const rruleObj = computed(() => {
    const _rrule = toValue(rrule)
    const _override = toValue(override)
    if (!_rrule) return null

    const byWeekdayOverride = match(_override?.byWeekday)
      .with(P.nullish, () => undefined)
      .with(P.string.regex(/^\s*$/), () => undefined)
      .with(P.string, (v) => parseInt(v))
      .with(P.number, (v) => v)
      .exhaustive()

    const byWeekday = match(_rrule.byWeekdays)
      .with(P.nullish, () => undefined)
      .with(P.string.regex(/^\s*$/), () => undefined)
      .with(P.array(P.number), (v) => v)
      .with(P.string, (v) => v.split(',').map((n) => parseInt(n)))
      .exhaustive()

    return new RRuleObject({
      freq: _rrule.freq,
      count: _rrule.count ?? undefined,
      bysetpos: _rrule.bySetPos || undefined,
      byweekday: typeof byWeekdayOverride === 'number' ? [byWeekdayOverride] : byWeekday,
      interval: _rrule.interval ?? undefined,
    })
  })

  const label = computed(() => {
    const _rruleObj = toValue(rruleObj)
    if (!_rruleObj) return ''
    try {
      return getNaturalLanguageRRuleRepresentation(_rruleObj.options)
    } catch {
      // Our rrule to natural language conversion failed.
      // We need to inform the user.
      return t('rrule.unknown')
    }
  })

  const attrs = computed(() => ({
    title: toValue(rruleObj.value)?.toString() ?? '',
  }))

  return { label, attrs }
}
